import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Download, Loader2, FileJson } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

export const DataExportSection: React.FC = () => {
  const { user } = useAuth();
  const [exporting, setExporting] = useState(false);

  const handleExportData = async () => {
    if (!user?.id) return;

    try {
      setExporting(true);
      
      const { data, error } = await supabase.functions.invoke('export-user-data', {
        body: { userId: user.id }
      });

      if (error) throw error;

      // Create and download the file
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `paperverse-data-export-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast.success('Data exported successfully', {
        description: 'Your data has been downloaded as a JSON file.'
      });
    } catch (error: any) {
      console.error('Error exporting data:', error);
      toast.error('Failed to export data', {
        description: error.message || 'Please try again later'
      });
    } finally {
      setExporting(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileJson className="h-5 w-5" />
          Download Your Data
        </CardTitle>
        <CardDescription>
          Get a copy of all your personal data
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Download a complete copy of your profile, papers, comments, reviews, consent records and
          your PaperVerse Writer manuscripts (LaTeX source, sections and bibliography) in a portable
          JSON format.
        </p>

        <p className="text-xs text-muted-foreground">
          After a paid plan ends or your account is closed, you can still export your manuscripts for
          30 days (Section 327p BGB). We recommend downloading a copy before you cancel.
        </p>

        <Button 
          onClick={handleExportData} 
          disabled={exporting}
          className="w-full sm:w-auto"
        >
          {exporting ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Preparing Export...
            </>
          ) : (
            <>
              <Download className="h-4 w-4 mr-2" />
              Download My Data
            </>
          )}
        </Button>
      </CardContent>
    </Card>
  );
};
